import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTripTypes } from '../hooks/useTripTypes'
import { useItems } from '../hooks/useItems'
import ItemForm from './ItemForm'
import type { Item, ItemPriority, CreateItemInput } from '../types'

const priorityConfig = {
  necessary: { 
    label: 'Necessary', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: '🟢'
  },
  'good-to-have': { 
    label: 'Good to Have', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: '🟠'
  },
  comfort: { 
    label: 'Comfort', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: '🔴'
  },
}

export default function TripTypeDetail() {
  const { id } = useParams<{ id: string }>()
  const { tripTypes } = useTripTypes()
  const { items, getItemsForTripType, removeItemFromTripType, createItem, addItemToTripType } = useItems()
  const [showForm, setShowForm] = useState(false)

  const tripType = tripTypes.find(tt => tt.id === id)
  const tripTypeItems = tripType ? getItemsForTripType(tripType.id) : []

  const handleAddItem = () => {
    setShowForm(true)
  }

  const handleFormSubmit = async (data: { item: CreateItemInput | Item, priority: ItemPriority }) => {
    if (!tripType) return

    try {
      let itemToAdd: Item

      // Check if it's a new item or existing item
      if ('id' in data.item) {
        // Existing item
        itemToAdd = data.item
      } else {
        // New item - create it first
        itemToAdd = await createItem(data.item)
      }

      // Add the item to the trip type with the selected priority
      await addItemToTripType({
        tripTypeId: tripType.id,
        itemId: itemToAdd.id,
        priority: data.priority
      })

      setShowForm(false)
    } catch (error) {
      console.error('Failed to add item:', error)
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
  }

  if (!tripType) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Trip Type Not Found</h2>
        <Link to="/trip-types" className="text-blue-600 hover:text-blue-800">
          ← Back to Trip Types
        </Link>
      </div>
    )
  }

  const getPriorityBadge = (priority: ItemPriority) => {
    const config = priorityConfig[priority]
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.color}`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    )
  }

  const groupedItems = {
    necessary: tripTypeItems.filter(item => item.priority === 'necessary'),
    'good-to-have': tripTypeItems.filter(item => item.priority === 'good-to-have'),
    comfort: tripTypeItems.filter(item => item.priority === 'comfort'),
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/trip-types" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
          ← Back to Trip Types
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{tripType.name}</h1>
            {tripType.description && (
              <p className="text-gray-600 mt-2">{tripType.description}</p>
            )}
            <p className="text-sm text-gray-400 mt-1">
              {tripTypeItems.length} items • Created {tripType.createdAt.toLocaleDateString()}
            </p>
          </div>
          <button 
            onClick={handleAddItem}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Item
          </button>
        </div>
      </div>

      {/* Item Form */}
      {showForm && (
        <div className="mb-6">
          <ItemForm
            tripTypeId={tripType.id}
            existingItems={items}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {/* Items by Priority */}
      {tripTypeItems.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 text-lg mb-4">No items in this trip type yet</p>
          <p className="text-gray-400">Add your first item to get started</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([priority, priorityItems]) => (
            <div key={priority} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                {getPriorityBadge(priority as ItemPriority)}
                <span className="text-gray-600">({priorityItems.length} items)</span>
              </div>
              
              {priorityItems.length === 0 ? (
                <p className="text-gray-400 text-sm">No {priorityConfig[priority as ItemPriority].label.toLowerCase()} items</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {priorityItems.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900 text-sm">{item.name}</h3>
                        <button
                          onClick={() => removeItemFromTripType(item.tripTypeItemId)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                      {item.description && (
                        <p className="text-gray-600 text-xs">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}